import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Divider,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Home as HomeIcon,
  Description as DocumentIcon,
  ShoppingCart as CartIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  List as ListIcon,
  Search as SearchIcon,
  Help as HelpIcon,
  Menu as MenuIcon,
  ArrowForward as ArrowForwardIcon,
  AttachFile as AttachFileIcon,
} from "@mui/icons-material";
import styled from "styled-components";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CustomButton from "../common-components/CustomButton";
import {
  DocumentUploadIcon,
  PreviewIcon,
} from "../../assets/Icon/dashboredIcon";
import { useSelector } from "react-redux";
import { getUrl } from "../../stores/incorporation";
import { useAppDispatch } from "../../stores/store";
import {
  getTaxFilingData,
  getTaxFilingDataById,
  uploadTaxFilingForm,
} from "../../stores/taxfiling";
import PreviewModal from "../itin/PreviewModal";
import { parse } from "path";
import { setToast } from "../../stores/toast/toastslice";

const TaxReviewPage = () => {
  const [activeSection, setActiveSection] = useState("document");
  const navigate = useNavigate();
  const location = useLocation();
  const { id: taxFilingId } = useParams();
  //  console.log(taxFilingId,"jjjj")

  const formDataFromState = location.state?.formData;
  // console.log(formDataFromState,"formDataFromState");
  const documentsFromState = location.state?.documents;
  const baseUrl = process.env.REACT_APP_BASE_IMAGE_URL || "";
  const { taxFilingDataReview } = useSelector((state: any) => state.taxfiling);
  const { countryList } = useSelector((store: any) => store.incorporation);
  // console.log(taxFilingDataReview,"taxFilingDataReview");
  const [openPreview, setOpenPreview] = useState(false);
  const [img, setImg] = useState("");
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const dispatch = useAppDispatch();
  const { taxfillingOrderList, status } = useSelector(
    (state: any) => state.taxfiling,
  );

  const getTaxFilingList = useCallback(async () => {
    if (!taxFilingId) return;
    const payload = { tax_filing_file_id: taxFilingId };
    await dispatch<any>(getTaxFilingDataById(payload)).then(
      async (res: any) => {
        if (res?.payload?.status >= 400) {
          await dispatch<any>(
            setToast({
              message:
                res?.payload?.data?.message ||
                (typeof res?.payload?.data?.message === "string" &&
                  res?.payload?.data?.message) ||
                "Something went wrong!",
              type: "error",
              title: "Error",
            }),
          );
          const timeout = setTimeout(() => {
            navigate("/tools/tax-filing/list");
          }, 2000);

          return () => clearTimeout(timeout);
        }
        setLoading(false);
      },
    );
  }, [dispatch, taxFilingId]);
  const orderItemId =
    taxFilingDataReview?.tax_filing_file?.order_item_id || null;
  // console.log(taxFilingDataReview?.tax_filing_file,"taxFilingDataReview");
  useEffect(() => {
    getTaxFilingList();
  }, []);

  // Extract data from navigation state or taxFilingDataReview
  const formData = useMemo(() => {
    // Prioritize form data from navigation state
    if (formDataFromState) {
      return formDataFromState;
    }
    // Fallback to API data
    if (taxFilingDataReview?.tax_filing_file?.file_data) {
      return taxFilingDataReview.tax_filing_file.file_data;
    }
    return null;
  }, [formDataFromState, taxFilingDataReview]);

  // Get country name from country ID
  const getCountryName = (countryId: number) => {
    const country = countryList?.find((c: any) => c.id === countryId);
    return country?.name || "N/A";
  };

  // Get option value (Yes/No) from numeric value
  const getOptionValue = (value: number) => {
    return value === 1 ? "Yes" : value === 2 ? "No" : "N/A";
  };
  const handleDownloadUrl = async (url: string) => {
    try {
      setDownloadingFile(url);
      const response = await dispatch<any>(getUrl({ url: url }));
      const fileUrl = response?.payload?.data;
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = fileUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      setDownloadingFile(null);
      // Optionally, display an error message to the user
    } finally {
      setDownloadingFile(null);
    }
  };
  // console.log(img,"img");
  // Dynamic data based on fetchListOrder
  const personalInfo = {
    name: formData?.name_surname || "N/A",
    email: formData?.email || "N/A",
    dateOfBirth: formData?.date_of_birth || "N/A",
    phone: formData?.phone_number
      ? `${formData.country_code || ""}${formData.phone_number}`
      : formData?.phone || "N/A",
    country: formData?.country ? formData?.country : "N/A",
    option1: formData?.working_outside_us
      ? formData?.working_outside_us
      : "N/A",
    option2: formData?.working_within_us ? formData?.working_within_us : "N/A",
  };

  const businessInfo = {
    address: formData?.business_address || "N/A",
    natureOfBusiness: formData?.nature_of_business || "N/A",
  };

  const financialData = {
    grossIncome: parseFloat(formData?.gross_income || "0"),
    returns: parseFloat(formData?.amount_of_returns || "0"),
    costOfGoodsSold: parseFloat(formData?.cost_of_goods_sold || "0"),
    advertising: parseFloat(formData?.advertising || "0"),
    commissions: parseFloat(formData?.commissions_fees || "0"),
    contractLabor: parseFloat(formData?.contract_labor || "0"),
    insurance: parseFloat(formData?.insurance || "0"),
    interest: parseFloat(formData?.interest_bank || "0"),
    legalServices: parseFloat(formData?.legal_professional || "0"),
    officeExpense: parseFloat(formData?.office_expenses || "0"),
    pension: parseFloat(formData?.pension_profit_sharing || "0"),
    rent: parseFloat(formData?.rent_lease_vehicles || "0"),
    repairs: parseFloat(formData?.repairs_maintenance || "0"),
    suppliesTech: parseFloat(formData?.supplies_materials || "0"),
    suppliesFurniture: parseFloat(formData?.supplies_stationary || "0"),
    taxes: parseFloat(formData?.taxes_licenses || "0"),
    travel: parseFloat(formData?.travel_meals || "0"),
    utilities: parseFloat(formData?.utilities || "0"),
    wages: parseFloat(formData?.wages || "0"),
    other: parseFloat(formData?.other_business || "0"),
    personalGrossIncome: parseFloat(formData?.personal_gross_income || "0"), // Not in the data structure
    taxableInterest: parseFloat(formData?.interest_income || "0"),
    taxExemptInterest: parseFloat(formData?.tax_exempt_interest || "0"),
    qualifiedDividends: parseFloat(formData?.qualified_dividends || "0"),
    taxableRefunds: parseFloat(formData?.taxable_refunds || "0"),
    scholarship: parseFloat(formData?.scholarship_fellowship_grants || "0"), // Not in the data structure
    capitalGain: parseFloat(formData?.capital_gain_loss || "0"),
    otherGains: parseFloat(formData?.other_gains_losses || "0"),
    iras: parseFloat(formData?.ira_distributions || "0"),
    rental: parseFloat(formData?.pensions_annuities || "0"),
    farmIncome: parseFloat(formData?.farm_income_loss || "0"),
    unemployment: parseFloat(formData?.unemployment_compensation || "0"),
    otherIncome: parseFloat(formData?.other_income || "0"),
    // passport: formData?.passport_scan || taxFilingDataReview?.documents_by_type?.passport || [],
  };
  // console.log(taxFilingDataReview?.tax_filing_file?.passport,"documentsFromState?.passport_scan");
  const summaryData = {
    totalContributions: parseFloat(formData?.total_contributions || "0"),
    totalDistributions: parseFloat(formData?.total_distributions || "0"),
    companyDocuments:
      taxFilingDataReview?.documents_by_type?.company ||
      documentsFromState?.company ||
      [],
    ein:
      taxFilingDataReview?.documents_by_type?.ein ||
      documentsFromState?.ein ||
      [],
    itin:
      taxFilingDataReview?.documents_by_type?.itin ||
      documentsFromState?.itin ||
      [],
    passport_scan:
      taxFilingDataReview?.tax_filing_file?.passport ||
      documentsFromState?.passport ||
      "",
    // passport_scan: documentsFromState?.passport || (taxFilingDataReview?.tax_filing_file?.passport?.path ? [taxFilingDataReview.tax_filing_file.file_data.passport_scan.path] : []),
  };
  // Get tax filing type
  const taxFilingType = taxFilingDataReview?.tax_filing_file?.filling_type;

  // Conditional title based on type
  const getTitle = () => {
    switch (taxFilingType) {
      case "Crop":
        return "Almost done, review your C-Corp information Multimember form";
      case "Multi":
        return "Almost done, review your Multi-Member LLC information";
      case "Single":
      default:
        return "Almost done, review your Single-Member LLC information";
    }
  };

  // Check if field should be shown based on type
  const shouldShowField = (fieldName: string) => {
    switch (taxFilingType) {
      case "Crop":
        // C-Corp specific fields
        return ![
          "option1",
          "option2",
          "totalContributions",
          "totalDistributions",
        ].includes(fieldName);
      case "Multi":
        // Multi-Member specific fields
        return ![
          "option1",
          "option2",
          "totalContributions",
          "totalDistributions",
        ].includes(fieldName);
      case "Single":
      default:
        // Single-Member shows all fields
        return true;
    }
  };
  // console.log(summaryData, "summaryData");

  const formatCurrency = (amount: number) => {
    return `$${amount}`;
  };
  const handleClose = () => setOpenPreview(false);

  const handleSaveAndStartOrder = async () => {
    // if (!formDataFromState) {
    //   alert("No form data available to submit");
    //   return;
    // }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append("tax_filing_file_id", taxFilingId || "");
      formData.append("step", "2");
      formData.append("save_as_draft", "false"); // Save as draft false

      console.log("Submitting final form data...");
      const result = await dispatch<any>(uploadTaxFilingForm(formData));

      if (result?.payload?.status === 1) {
        console.log("Form submitted successfully!");
        navigate(`/tools/tax-filing/tax-detail/${taxFilingId}/${orderItemId}`, {
          state: {
            orderItemId: orderItemId,
            taxFilingDataReview: taxFilingDataReview,
          },
        });
      } else {
        // alert("Failed to submit form. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      // alert("Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // console.log(img,"personalInfo");

  const InfoRow = ({
    label,
    value,
    isLink = false,
  }: {
    label: string;
    value: any;
    isLink?: boolean;
  }) => {
    // Check if this is a document field that should show icons
    const isDocumentField =
      label === "EIN" ||
      label === "ITIN" ||
      label === "Company Documents" ||
      label === "Passport";

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          py: 1.5,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <Box sx={{ width: "50%" }}>
          <Typography variant="body2" sx={{ color: "#666", fontWeight: 500 }}>
            {label}:
          </Typography>
        </Box>

        <Box sx={{ width: "50%" }}>
          {isDocumentField && label.toLowerCase() === "passport" && value ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
              }}
            >
              <span
                onClick={() => {
                  setOpenPreview(true);
                  setImg(`${baseUrl}${value}`);
                }}
              >
                <PreviewIcon />
              </span>
              <span onClick={() => handleDownloadUrl(value)}>
                <DocumentUploadIcon />
              </span>
            </Box>
          ) : isDocumentField && Array.isArray(value) && value.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {value.map((doc: any, index: number) => (
                <Box
                  key={doc.id || index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    justifyContent: { xs: "flex-end", sm: "flex-start" },
                  }}
                >
                  {/* <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
                    Document {index + 1}:
                  </Typography> */}
                  <span
                    onClick={() => {
                      setOpenPreview(true);
                      setImg(`${baseUrl}${doc.document_file_url}`);
                    }}
                  >
                    <PreviewIcon />
                  </span>
                  <span
                    onClick={() => handleDownloadUrl(doc.document_file_url)}
                  >
                    <DocumentUploadIcon />
                  </span>
                </Box>
              ))}
            </Box>
          ) : isDocumentField &&
            (!Array.isArray(value) || value.length === 0) ? (
            <Typography
              variant="body2"
              sx={{ color: "#666", fontSize: "12px" }}
            >
              No documents uploaded
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: isLink ? "#2977CC" : "#333",
                fontWeight: 500,
                cursor: isLink ? "pointer" : "default",
                textDecoration: isLink ? "underline" : "none",
                textAlign: { xs: "right", sm: "left" },
                wordBreak: "break-all",
                overflowWrap: "break-word",
                maxWidth: "100%",
              }}
            >
              {value}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const DocumentLink = ({ filename }: { filename: string }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
      <AttachFileIcon sx={{ fontSize: 16, color: "#666" }} />
      <Typography
        variant="body2"
        sx={{
          color: "#2977CC",
          cursor: "pointer",
          textDecoration: "underline",
          "&:hover": { textDecoration: "none" },
        }}
      >
        {filename}
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <>
      <StyledMainContent>
        <StyledContentArea>
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, mb: 4, color: "#333" }}
          >
            {getTitle()}
          </Typography>

          {/* Personal Information Section */}
          <StyledSection>
            <StyledInfoBox>
              <InfoRow label="Name and Surname" value={personalInfo.name} />
              <InfoRow label="E-Mail" value={personalInfo.email} />
              <InfoRow label="Date of Birth" value={personalInfo.dateOfBirth} />
              <InfoRow label="Phone" value={personalInfo.phone} />
              <InfoRow label="Country" value={personalInfo.country} />
              {shouldShowField("option1") && (
                <InfoRow
                  label="Bussiness Details(Option 1) "
                  value={personalInfo.option1}
                />
              )}
              {shouldShowField("option2") && (
                <InfoRow
                  label="Bussiness Details(Option 2)"
                  value={personalInfo.option2}
                />
              )}
              <InfoRow label="Business Address" value={businessInfo.address} />
              <InfoRow
                label="Nature of Business"
                value={businessInfo.natureOfBusiness}
              />
              <InfoRow
                label="Gross Income (USD)"
                value={formatCurrency(financialData.grossIncome)}
              />
              <InfoRow
                label="Amount of Returns (USD)"
                value={formatCurrency(financialData.returns)}
              />
              <InfoRow
                label="Amount of Cost of Goods Sold (USD)"
                value={formatCurrency(financialData.costOfGoodsSold)}
              />
              <InfoRow
                label="Advertising (USD)"
                value={formatCurrency(financialData.advertising)}
              />
              <InfoRow
                label="Commissions and Fees (USD)"
                value={formatCurrency(financialData.commissions)}
              />
              <InfoRow
                label="Contract Labor (USD)"
                value={formatCurrency(financialData.contractLabor)}
              />
              <InfoRow
                label="Insurance (other than health) (USD)"
                value={formatCurrency(financialData.insurance)}
              />
              <InfoRow
                label="Interest (paid to the banks, etc.) (USD)"
                value={formatCurrency(financialData.interest)}
              />
              <InfoRow
                label="Legal and Professional Services (USD)"
                value={formatCurrency(financialData.legalServices)}
              />
              <InfoRow
                label="Office Expense (USD)"
                value={formatCurrency(financialData.officeExpense)}
              />
              <InfoRow
                label="Pension and Profit Sharing (USD)"
                value={formatCurrency(financialData.pension)}
              />
              <InfoRow
                label="Rent or Lease: Vehicles, Machinery and equipment, Other business property (rent) (USD)"
                value={formatCurrency(financialData.rent)}
              />
              <InfoRow
                label="Repairs and Maintenance (USD)"
                value={formatCurrency(financialData.repairs)}
              />
              <InfoRow
                label="Supplies (technology/software) (USD)"
                value={formatCurrency(financialData.suppliesTech)}
              />
              <InfoRow
                label="Supplies (furniture) (USD)"
                value={formatCurrency(financialData.suppliesFurniture)}
              />
              <InfoRow
                label="Taxes and Licenses (USD)"
                value={formatCurrency(financialData.taxes)}
              />
              <InfoRow
                label="Travel and Meals (50% countable) (USD)"
                value={formatCurrency(financialData.travel)}
              />
              <InfoRow
                label="Utilities (USD)"
                value={formatCurrency(financialData.utilities)}
              />
              <InfoRow
                label="Wages (USD)"
                value={formatCurrency(financialData.wages)}
              />
              <InfoRow
                label="Other (USD)"
                value={formatCurrency(financialData.other)}
              />
              <InfoRow
                label="Personal Gross Income (USD)"
                value={formatCurrency(financialData.personalGrossIncome)}
              />
              <InfoRow
                label="Taxable interest Income (USD)"
                value={formatCurrency(financialData.taxableInterest)}
              />
              <InfoRow
                label="Tax-exempt interest Income (USD)"
                value={formatCurrency(financialData.taxExemptInterest)}
              />
              <InfoRow
                label="Qualified dividends Income (USD)"
                value={formatCurrency(financialData.qualifiedDividends)}
              />
              <InfoRow
                label="Taxable refunds, credits, or offsets of state and local income taxes (USD)"
                value={formatCurrency(financialData.taxableRefunds)}
              />
              <InfoRow
                label="Scholarship and fellowship grants (USD)"
                value={formatCurrency(financialData.scholarship)}
              />
              <InfoRow
                label="Capital gain or (loss) (USD)"
                value={formatCurrency(financialData.capitalGain)}
              />
              <InfoRow
                label="Other gains or (losses) (USD)"
                value={formatCurrency(financialData.otherGains)}
              />
              <InfoRow
                label="IRAs, pensions, and annuities (USD)"
                value={formatCurrency(financialData.iras)}
              />
              <InfoRow
                label="Rental real estate, royalties, partnerships, trusts, etc. (USD)"
                value={formatCurrency(financialData.rental)}
              />
              <InfoRow
                label="Farm income or (loss). (USD)"
                value={formatCurrency(financialData.farmIncome)}
              />
              <InfoRow
                label="Unemployment compensation (USD)"
                value={formatCurrency(financialData.unemployment)}
              />
              <InfoRow
                label="Other income (USD)"
                value={formatCurrency(financialData.otherIncome)}
              />
              <InfoRow
                label="Total Contributions"
                value={formatCurrency(summaryData.totalContributions)}
              />
              <InfoRow
                label="Total Distributions"
                value={formatCurrency(summaryData.totalDistributions)}
              />
              <InfoRow
                label="Company Documents"
                value={summaryData.companyDocuments}
              />
              <InfoRow label="EIN" value={summaryData.ein} isLink={true} />
              <InfoRow label="ITIN" value={summaryData.itin} isLink={true} />
              <InfoRow
                label="Passport"
                value={summaryData.passport_scan}
                isLink={true}
              />
            </StyledInfoBox>
          </StyledSection>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 4,
              pt: 3,
              borderTop: "2px solid #f0f0f0",
              flexWrap: "wrap",
            }}
          >
            {/* <CustomButton
              btnTitle="Next"
              type="submit"
              loading={false}
              handleClick={()=>navigate("/tools/tax-filing/tax-detail")}
            /> */}
            <Button
              variant="outlined"
              sx={{
                width: { xs: "100%", sm: "auto" },
                px: 4,
                py: 1.5,
                borderColor: "#ccc",
                color: "#666",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#999",
                  backgroundColor: "#f5f5f5",
                },
              }}
              onClick={() => {
                navigate(`/tools/tax-filing/single-member/${taxFilingId}`, {
                  state: { isReturn: true, taxFilingId: taxFilingId },
                });
              }}
            >
              ← Return
            </Button>
            <CustomButton
              btnTitle="Save and start order"
              type="button"
              loading={isSubmitting}
              handleClick={handleSaveAndStartOrder}
            />
          </Box>
        </StyledContentArea>
      </StyledMainContent>
      <PreviewModal open={openPreview} handleClose={handleClose} img={img} />
    </>
  );
};

// Styled Components
const StyledContainer = styled(Box)({
  display: "flex",
  height: "100vh",
  backgroundColor: "#f8f9fa",
});

const StyledMainContent = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  backgroundColor: "white",
});

const StyledHeader = styled(Box)({
  height: "60px",
  borderBottom: "1px solid #e0e0e0",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 20px",
  backgroundColor: "white",
});

const StyledContentArea = styled(Box)({
  flex: 1,
  padding: "10px",
  "@media (min-width: 600px)": {
    padding: "40px",
  },
  overflowY: "auto",
  backgroundColor: "white",
});

const StyledSection = styled(Box)({
  marginBottom: "32px",
});

const StyledInfoBox = styled(Box)({
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  padding: "20px",
  // backgroundColor: '#fafafa',
});

export { TaxReviewPage };
export default TaxReviewPage;
